import { inputToTags, tagsToInput } from "../../mst/kinds/utils";
import { getDefaultBaseInput, OptimisticConcurrency } from "../../schema/types/base";
import { CreateCloudAccount } from "../../schema/types/cloudaccount/create";
import {
  CloudAccount,
  CloudAccountAwsConfig,
  CloudAccountAzureConfig,
  CloudAccountGcpConfig,
  CloudAccountNgsConfig,
} from "../../schema/types/cloudaccount/entity";
import {
  CloudAccountCloudProvider,
  CloudAccountConfigAzureCliType,
  CloudAccountConfigAzureType,
  CloudAccountInput,
} from "../../schema/types/cloudaccount/input";
import { homeLink, request, resourceLink } from "../../services/cpln";
import { randomReadableName } from "../../services/utils";

export function getDefaultCloudAccountInput(): CloudAccountInput {
  return {
    ...getDefaultBaseInput(),
    version: 0,
    id: "",
    provider: "aws",
    aws: {
      method: "roleArn",
      roleArn: "",
      accountId: "",
      instructions: {},
    },
    azure: {
      secret: {
        value: "",
        link: "",
      },
      type: "connector",
      cliType: !navigator.userAgent.toLowerCase()?.includes("mac os") ? "windows" : "linux/mac",
      functionAppName: randomReadableName(),
      guideOpened: false,
      locationName: "",
      resourceGroupName: "",
      storageAccountName: "",
      url: "",
      code: "",
      subscriptionId: "",
    },
    gcp: {
      projectId: "",
      showGuide: false,
      instructions: {},
    },
    ngs: {
      secret: {
        value: "",
        link: "",
      },
    },
    meta: {
      isEdit: false,
      drawer: "",
    },
  };
}

export function inputToCloudAccountPartial(input: CloudAccountInput): CreateCloudAccount {
  const res: CreateCloudAccount = {
    name: input.name,
    kind: "cloudaccount",
    tags: inputToTags(input.tags),
    provider: input.provider,
    data: {} as any,
  };
  if (input.description) {
    res.description = input.description;
  }
  if (input.provider === "aws") {
    if (input.aws.method === "accountId") {
      const roleArn = `arn:aws:iam::${input.aws.accountId}:role/${input.aws.instructions.data.bridgeRole}`;
      (res.data as CloudAccountAwsConfig).roleArn = roleArn;
    } else {
      (res.data as CloudAccountAwsConfig).roleArn = input.aws.roleArn;
    }
  } else if (input.provider === "azure") {
    (res.data as CloudAccountAzureConfig).secretLink =
      input.azure.type === "connector" ? "<will-be-generated-on-create>" : input.azure.secret.link;
  } else if (input.provider === "gcp") {
    (res.data as CloudAccountGcpConfig).projectId = input.gcp.projectId;
  } else if (input.provider === "ngs") {
    (res.data as CloudAccountNgsConfig).secretLink = input.ngs.secret.link;
  }

  return res;
}

export async function asyncInputToCloudAccount(
  input: CloudAccountInput,
): Promise<{ body: CreateCloudAccount; secretLink: string }> {
  let secretLink = "";
  const res = inputToCloudAccountPartial(input);
  if (input.provider === "azure") {
    if (input.azure.type === "connector") {
      const azureSecretName = `${input.name}-access`;
      const secretBody: any = {
        name: azureSecretName,
        type: "azure-connector",
        data: {
          url: input.azure.url,
          code: input.azure.code,
        },
      };
      await request({ method: "put", body: secretBody, url: homeLink("secret") });
      secretLink = resourceLink("secret", azureSecretName);
    } else {
      secretLink = input.azure.secret.link;
    }
    (res.data as CloudAccountAzureConfig).secretLink = secretLink;
  }

  return { body: res, secretLink: secretLink };
}

export function cloudAccountToInput(cloudAccount: CloudAccount): CloudAccountInput {
  const def = getDefaultCloudAccountInput();
  const res: CloudAccountInput = {
    ...def,
    version: cloudAccount.version,
    id: cloudAccount.id,
    name: cloudAccount.name,
    description: cloudAccount.description,
    tags: tagsToInput(cloudAccount.tags),
    provider: cloudAccount.provider,
    meta: {
      ...def.meta,
      isEdit: true,
    },
  };
  if (cloudAccount.provider === "aws") {
    res.aws.roleArn = (cloudAccount.data as CloudAccountAwsConfig).roleArn;
  } else if (cloudAccount.provider === "azure") {
    res.azure.type = "secret"; // When editing, secret already exists
    res.azure.secret = {
      value: (cloudAccount.data as CloudAccountAzureConfig).secretLink,
      link: (cloudAccount.data as CloudAccountAzureConfig).secretLink,
    };
  } else if (cloudAccount.provider === "gcp") {
    res.gcp.projectId = (cloudAccount.data as CloudAccountGcpConfig).projectId;
  } else if (cloudAccount.provider === "ngs") {
    res.ngs = {
      secret: {
        value: (cloudAccount.data as CloudAccountNgsConfig).secretLink,
        link: (cloudAccount.data as CloudAccountNgsConfig).secretLink,
      },
    };
  }

  return res;
}

export function cloudAccountToPatch(_cloudAccount: CreateCloudAccount, concurrency: OptimisticConcurrency) {
  const cloudAccount: any = JSON.parse(JSON.stringify(_cloudAccount));

  cloudAccount[`$replace/tags`] = cloudAccount.tags;
  delete cloudAccount.tags;

  cloudAccount.id = concurrency.id;
  cloudAccount.version = concurrency.version;

  return cloudAccount;
}

export const CloudAccountCloudProviderOptions: { label: string; value: CloudAccountCloudProvider }[] = [
  {
    label: "AWS",
    value: "aws",
  },
  {
    label: "Azure",
    value: "azure",
  },
  {
    label: "GCP",
    value: "gcp",
  },
  {
    label: "NGS",
    value: "ngs",
  },
];

export const CloudAccountConfigAzureTypeOptions: { label: string; value: CloudAccountConfigAzureType }[] = [
  {
    label: "Create Azure Connector Secret",
    value: "connector",
  },
  {
    label: "Select Existing Connector Secret",
    value: "secret",
  },
];

export const CloudAccountConfigAzureCliTypeOptions: { label: string; value: CloudAccountConfigAzureCliType }[] = [
  {
    label: "Linux/Mac",
    value: "linux/mac",
  },
  {
    label: "Windows",
    value: "windows",
  },
];

export const CloudAccountConfigAzureFunctionLocationOptions = [
  {
    label: "Central US",
    value: "centralus",
  },
  {
    label: "East US",
    value: "eastus",
  },
  {
    label: "East US 2",
    value: "eastus2",
  },
  {
    label: "France Central",
    value: "francecentral",
  },
  {
    label: "Germany West Central",
    value: "germanywestcentral",
  },
  {
    label: "North Central US",
    value: "northcentralus",
  },
  {
    label: "North Europe",
    value: "northeurope",
  },
  {
    label: "Norway East",
    value: "norwayeast",
  },
  {
    label: "Switzerland North",
    value: "switzerlandnorth",
  },
  {
    label: "UK South",
    value: "uksouth",
  },
  {
    label: "UK West",
    value: "ukwest",
  },
  {
    label: "West Central US",
    value: "westcentralus",
  },
  {
    label: "West Europe",
    value: "westeurope",
  },
  {
    label: "West US",
    value: "westus",
  },
  {
    label: "West US 2",
    value: "westus2",
  },
  {
    label: "West US 3",
    value: "westus3",
  },
];
